import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  TablePagination,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import UserGuidesTable from "../components/UserGuidesTable";
import {
  fetchUserGuides,
  deleteUserGuide,
  setSearchTerm,
  setCurrentPage,
  setItemsPerPage,
  setSortBy,
  setSortOrder,
} from "../redux/slices/userGuidesSlice";

const UserGuides = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    guideId: null,
    guideName: "",
  });

  const {
    guides,
    loading,
    error,
    searchTerm,
    currentPage,
    itemsPerPage,
    totalItems,
    sortBy,
    sortOrder,
  } = useSelector((state) => state.userGuides);

  const handleSidebarStateChange = (isOpen) => {
    setSidebarOpen(isOpen);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  // Fetch guides when filters change
  useEffect(() => {
    dispatch(
      fetchUserGuides({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        sortBy,
        sortOrder,
      })
    );
  }, [dispatch, currentPage, itemsPerPage, searchTerm, sortBy, sortOrder]);

  const handleSearchChange = (event) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleChangePage = (event, newPage) => {
    dispatch(setCurrentPage(newPage + 1)); // MUI pagination is 0-based, API is 1-based
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setItemsPerPage(parseInt(event.target.value, 10)));
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    dispatch(setSortBy(property));
    dispatch(setSortOrder(isAsc ? "desc" : "asc"));
  };

  const handleRefresh = () => {
    dispatch(
      fetchUserGuides({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        sortBy,
        sortOrder,
      })
    );
  };

  const handleCreateNew = () => {
    navigate("/UserGuides/create");
  };

  const handleEdit = (guideId) => {
    navigate(`/UserGuides/edit/${guideId}`);
  };

  const handleDeleteClick = (guideId) => {
    const guide = guides.find((g) => g._id === guideId);
    setDeleteDialog({
      open: true,
      guideId,
      guideName: guide?.title || guide?.navigation_name || "this guide",
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.guideId) {
      try {
        const result = await dispatch(deleteUserGuide(deleteDialog.guideId));
        if (deleteUserGuide.fulfilled.match(result)) {
          showSnackbar("User guide deleted successfully");
          // If current page becomes empty, go to previous page
          if (guides.length === 1 && currentPage > 1) {
            dispatch(setCurrentPage(currentPage - 1));
          }
        } else {
          showSnackbar(result.payload || "Failed to delete guide", "error");
        }
      } catch (error) {
        showSnackbar("Failed to delete guide", "error");
      }
    }
    setDeleteDialog({ open: false, guideId: null, guideName: "" });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, guideId: null, guideName: "" });
  };

  return (
    <div>
      <Sidebar onSidebarStateChange={handleSidebarStateChange} />
      <Navbar sidebarOpen={sidebarOpen} />

      <Box
        sx={{
          ml: sidebarOpen ? "220px" : "100px",
          p: { xs: 2, sm: 3, md: 4, xl: 4 },
          backgroundColor: "#fafafa",
          minHeight: "100vh",
          transition: "margin-left 0.3s ease",
          fontFamily: "Outfit",
        }}
      >
        {/* Header Section */}
        <Box sx={{ marginBottom: "32px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <h1
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#2B3674",
                textAlign: "left",
                margin: "0 0 8px 0",
                lineHeight: "1.4",
                fontFamily: "Outfit",
              }}
            >
              User Guides
            </h1>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  borderColor: "#9048FB",
                  color: "#9048FB",
                  "&:hover": {
                    borderColor: "#7c3aed",
                    backgroundColor: "#f3e8ff",
                  },
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 3,
                  py: 1.5,
                  fontFamily: "Outfit",
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                sx={{
                  backgroundColor: "#9048FB",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#7c3aed",
                  },
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 3,
                  py: 1.5,
                  fontFamily: "Outfit",
                }}
              >
                Create New Guide
              </Button>
            </Box>
          </Box>
          <p
            style={{
              color: "#A3AED0",
              fontSize: "14px",
              margin: 0,
              textAlign: "left",
              fontWeight: 400,
              fontFamily: "Outfit",
            }}
          >
            Create and manage user guides for your platform
          </p>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by navigation name or title..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#A3AED0" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontFamily: "Outfit",
                backgroundColor: "#ffffff",
                "& fieldset": {
                  borderColor: "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: "#9048FB",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#9048FB",
                },
              },
              "& .MuiInputBase-input": {
                fontFamily: "Outfit",
                fontSize: "14px",
                color: "#374151",
                "&::placeholder": {
                  color: "#A3AED0",
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Main Content Card */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: { xs: "16px", sm: "24px", md: "32px", xl: "40px" },
            boxShadow:
              "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            border: "1px solid #f1f5f9",
          }}
        >
          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontFamily: "Outfit" }}>
              {error}
            </Alert>
          )}

          {/* Table */}
          <UserGuidesTable
            guides={guides}
            loading={loading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />

          {/* Pagination */}
          {guides.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={itemsPerPage}
              page={currentPage - 1} // MUI pagination is 0-based
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid #f1f5f9",
                mt: 2,
                "& .MuiTablePagination-toolbar": {
                  fontFamily: "Outfit",
                  color: "#374151",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontFamily: "Outfit",
                    fontSize: "14px",
                    color: "#6b7280",
                  },
                "& .MuiTablePagination-select": {
                  fontFamily: "Outfit",
                  fontSize: "14px",
                  color: "#374151",
                },
              }}
            />
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            fontFamily: "Outfit",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
          Delete User Guide
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: "Outfit" }}>
            Are you sure you want to delete "{deleteDialog.guideName}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              fontFamily: "Outfit",
              textTransform: "none",
              color: "#6b7280",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              fontFamily: "Outfit",
              textTransform: "none",
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            borderRadius: "12px",
            fontFamily: "Outfit",
            fontWeight: 500,
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            borderRadius: "12px",
            fontFamily: "Outfit",
            fontWeight: 500,
            "& .MuiAlert-message": {
              fontFamily: "Outfit",
            },
            "& .MuiAlert-icon": {
              fontSize: "20px",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserGuides;

